import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageHeader, PanelLoading } from '../components/ui.jsx'
import DataTable, { StatusPill } from '../components/DataTable.jsx'
import { formatDayTime } from '../lib/hubspot.js'
import { relativeTime } from '../lib/events.js'
import RolePermissions from '../components/RolePermissions.jsx'
import {
  CollaborationIcon,
  TrashIcon,
  UserIcon,
  TagIcon,
  CalendarIcon,
  PlusIcon,
} from '../components/icons.jsx'
import { initialsOf, useAuth } from '../auth.jsx'
import { changeOwnPassword, listMembers, removeMember, setMemberPassword, updateMember } from '../lib/team.js'
import {
  ROLE_DEFS,
  ROLE_KEYS,
  normalizeRole,
  can as roleCan,
  rolesAssignableBy,
  useRbac,
} from '../lib/rbac.js'
import { logEvent } from '../lib/events.js'

const STATUS_TONE = { Active: 'success', Invited: 'warning' }

// The Cognito pool's password policy (revailo-sales-platform-terraform
// modules/cognito/main.tf). Checked here so a typo is caught before the round
// trip; the pool itself is still the judge, and its refusal is shown verbatim.
const PASSWORD_RULES = [
  { label: '8+ characters', test: (p) => p.length >= 8 },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'A number', test: (p) => /[0-9]/.test(p) },
]

// Change a member's password: the admin types it, the lambda sets it as the
// member's permanent password and emails it to them in plain text. With
// `self`, it is your own password: Cognito checks your current one first, and
// the email comes to you.
function PasswordModal({ member, self = false, onClose, onDone }) {
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const passes = PASSWORD_RULES.every((r) => r.test(password))
  // Cognito refuses leading or trailing spaces; say so here rather than send it.
  const padded = password !== password.trim()
  const ready = passes && !padded && (!self || current !== '')

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  async function submit(e) {
    e.preventDefault()
    if (!ready || saving) return
    setSaving(true)
    setError('')
    try {
      const result = self ? await changeOwnPassword(current, password) : await setMemberPassword(member.email, password)
      onDone(result)
    } catch (err) {
      setError(err.message || 'The password could not be changed.')
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.45)] p-4 dark:bg-[rgba(0,0,0,0.6)]"
      onClick={() => !saving && onClose()}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={self ? 'Change your password' : `Change password for ${member.email}`}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] rounded-[14px] border border-line bg-surface p-6 shadow-card"
      >
        <div className="mb-4 flex items-center gap-x-3">
          <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[11px] bg-[#18181b] text-[13px] font-semibold tracking-[0.02em] text-white">
            {initialsOf(member.name || member.email)}
          </span>
          <div className="min-w-0">
            <h3 className="m-0 text-[16px] font-semibold text-ink">
              {self ? 'Change your password' : `Change password — ${member.name || member.email}`}
            </h3>
            <div className="truncate text-[12.5px] text-dim">{member.email}</div>
          </div>
        </div>

        {self && (
          <>
            <label htmlFor="own-current-password" className="mb-1.5 block text-[12.5px] font-semibold text-ink">
              Current password
            </label>
            <input
              id="own-current-password"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={saving}
              className="mb-4 w-full rounded-[9px] border border-line bg-panel px-[12px] py-[8px] text-[14px] text-ink outline-none transition-colors focus:border-primary"
            />
          </>
        )}

        <label htmlFor="member-new-password" className="mb-1.5 block text-[12.5px] font-semibold text-ink">
          New password
        </label>
        <div className="flex items-center gap-x-2">
          <input
            id="member-new-password"
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus={!self}
            disabled={saving}
            className="min-w-0 flex-1 rounded-[9px] border border-line bg-panel px-[12px] py-[8px] text-[14px] text-ink outline-none transition-colors focus:border-primary"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="flex-none rounded-[9px] border border-line bg-transparent px-[12px] py-[8px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
          >
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
        <ul className="m-0 mt-2.5 grid list-none grid-cols-2 gap-x-3 gap-y-1 p-0 text-[12.5px]">
          {PASSWORD_RULES.map((r) => (
            <li key={r.label} className={r.test(password) ? 'text-[#15803d] dark:text-[#4ade80]' : 'text-faint'}>
              {r.test(password) ? '✓' : '·'} {r.label}
            </li>
          ))}
        </ul>
        {padded && <p className="m-0 mt-2 text-[12.5px] text-[#b45309] dark:text-[#fbbf24]">No spaces at the start or end.</p>}

        <p className="m-0 mt-4 text-[12.5px] leading-[1.5] text-dim">
          {self
            ? `It takes effect immediately and you stay signed in here. We’ll email the new password to ${member.email} in plain text.`
            : `It takes effect immediately, and we’ll email the new password to ${member.email} in plain text.`}
        </p>
        {error && (
          <p role="alert" className="m-0 mt-3 rounded-[9px] border border-[#fecaca] bg-[#fef2f2] px-[12px] py-[8px] text-[12.5px] text-[#b91c1c] dark:border-[rgba(220,38,38,0.35)] dark:bg-[rgba(220,38,38,0.12)] dark:text-[#f87171]">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-x-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="inline-flex items-center rounded-[9px] border border-line bg-transparent px-[14px] py-[8px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!ready || saving}
            className="inline-flex items-center rounded-[9px] bg-primary px-[16px] py-[8px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}

// The legend derives from the live permission matrix, so it can never drift
// from what the app actually enforces.
const LEGEND_ROWS = [
  { label: 'Enrich leads', perm: 'leads.enrich' },
  { label: 'Export data', perm: 'leads.export' },
  { label: "Every rep's performance", perm: 'performance.team' },
  { label: 'Invite members', perm: 'members.invite' },
  { label: 'Change roles', perm: 'members.changeRole' },
]

// Reference material, not day-to-day UI — it lives behind a "What each role can
// do" link in the header so the page itself stays about the actual team.
function RolesLegendModal({ onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.45)] p-4 dark:bg-[rgba(0,0,0,0.6)]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What each role can do"
        className="max-h-[85vh] w-full max-w-[680px] overflow-y-auto rounded-[14px] border border-line bg-surface p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="m-0 mb-1 text-[16px] font-semibold text-ink">What each role can do</h3>
        <p className="m-0 mb-4 text-[12.5px] leading-[1.5] text-dim">
          Five presets. The owner runs the workspace; managers see every rep’s numbers; reps work
          their own book; viewers never touch data. Any member’s access can be fine-tuned from
          their Role cell.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="pb-2 pr-4 text-left font-medium text-dim" />
                {ROLE_KEYS.map((r) => (
                  <th key={r} className="px-2 pb-2 text-center font-semibold text-ink" title={ROLE_DEFS[r].blurb}>
                    {ROLE_DEFS[r].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LEGEND_ROWS.map((row) => (
                <tr key={row.perm} className="[&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
                  <td className="py-[7px] pr-4 text-dim">{row.label}</td>
                  {ROLE_KEYS.map((r) => (
                    <td key={r} className="px-2 py-[7px] text-center">
                      {/* Brand blue, matching the permission matrix this table
                          explains — a granted permission is the same fact in
                          both places and should not be two different colours. */}
                      {roleCan(r, row.perm) ? (
                        <svg viewBox="0 0 24 24" fill="none" className="mx-auto h-[14px] w-[14px] text-primary" aria-hidden="true">
                          {/* coolicons: line/Check.svg — was a bare "✓" text
                              character, which is a font glyph, not an icon. */}
                          <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-[9px] border border-line bg-transparent px-[14px] py-[8px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Collaboration() {
  const { user, impersonate } = useAuth()
  const { role: myRole, can, perms: myPerms } = useRbac()
  // "Sign in as" is an owner/admin power — the strongest view into the account,
  // so it rides with the same set that may change roles and remove members.
  const canImpersonate = myRole === 'owner' || myRole === 'admin'
  const navigate = useNavigate()
  const location = useLocation()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  // A fresh invite from the wizard hands us the invitee via navigation state.
  const [notice, setNotice] = useState('')
  // 'warning' when something the reader must act on went wrong — a password
  // that changed but could not be emailed.
  const [noticeTone, setNoticeTone] = useState('success')
  const [legendOpen, setLegendOpen] = useState(false)
  const [passwordFor, setPasswordFor] = useState(null)

  function handlePasswordDone({ emailed, emailError }) {
    const m = passwordFor
    const own = isMe(m)
    const who = m.name || m.email
    setPasswordFor(null)
    setNoticeTone(emailed || own ? 'success' : 'warning')
    if (own) {
      // You typed it, so a failed email costs nothing — say so plainly.
      setNotice(
        emailed
          ? `Your password was changed. A copy was emailed to ${m.email}.`
          : `Your password was changed. The email copy didn’t go out: ${emailError}`,
      )
    } else {
      setNotice(
        emailed
          ? `Password changed for ${who}. The new password was emailed to ${m.email}.`
          : `Password changed for ${who}, but the email didn’t go out: ${emailError} Give them the new password yourself.`,
      )
    }
    logEvent('member_password_changed', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: own
        ? `Changed their own password${emailed ? ' and emailed it to themselves' : ''}`
        : `Changed ${who}’s password${emailed ? ' and emailed it to them' : ' (email not sent)'}`,
    })
  }

  async function refresh() {
    setLoading(true)
    const { members } = await listMembers()
    setMembers(members)
    setLoading(false)
  }

  useEffect(() => {
    refresh()
  }, [])

  // Show a one-time confirmation when returning from the invite wizard, then
  // clear the navigation state so a refresh doesn't resurface it.
  useEffect(() => {
    if (location.state?.invited) {
      // Only claim an email went out when the real API handled the invite —
      // the local-dev fallback just records the row.
      setNoticeTone('success')
      setNotice(
        location.state.mode === 'local'
          ? `${location.state.invited} added to the roster. Offline mode — no email was sent; run against the deployed API for real invites.`
          : `Invite sent to ${location.state.invited}. They’ll get an email and can sign up to set their password.`,
      )
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate])

  async function handleRemove(m) {
    if (!can('members.remove')) return
    const isSelf = (m.email || '').toLowerCase() === (user?.email || '').toLowerCase()
    if (isSelf || (ROLE_DEFS[normalizeRole(m.role)]?.rank ?? 0) >= myRank) return
    if (!window.confirm(`Remove ${m.name || m.email} from the team?`)) return
    await removeMember(m.email)
    logEvent('member_removed', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `Removed ${m.name || m.email} from the team`,
    })
    await refresh()
  }

  // The access editor works on a draft so Cancel is a true no-op.
  const [editing, setEditing] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  function openEditor(m) {
    setEditing(m)
    setDraft({
      role: normalizeRole(m.role),
      permissions: Array.isArray(m.permissions) ? m.permissions : null,
    })
  }

  async function handleAccessSave() {
    if (!editing || !draft || !can('members.changeRole')) return
    setSaving(true)
    const isCustom = Array.isArray(draft.permissions)
    // Defense in depth: never persist a role outside what this actor may
    // assign — fall back to the member's existing base role.
    const nextRole = assignable.includes(draft.role) ? draft.role : normalizeRole(editing.role)
    try {
      await updateMember(editing.email, {
        role: nextRole,
        // Explicit null clears old overrides when snapping back to a preset.
        permissions: isCustom ? draft.permissions : null,
      })
    } catch (err) {
      setSaving(false)
      window.alert(err.message || 'Could not update the member.')
      return
    }
    logEvent('member_role_changed', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `Set ${editing.name || editing.email} to ${
        isCustom ? `custom permissions (${ROLE_DEFS[nextRole]?.label || nextRole} base)` : ROLE_DEFS[nextRole]?.label || nextRole
      }`,
    })
    setSaving(false)
    setEditing(null)
    await refresh()
  }

  function handleInvite() {
    navigate('/collaboration/invite')
  }

  const myRank = ROLE_DEFS[myRole]?.rank ?? 0
  const assignable = rolesAssignableBy(myRole)

  // The roster stores the name captured when a member was invited and never
  // re-reads it from Cognito, so renaming yourself in Settings would otherwise
  // leave your invite-time name sitting on your own row here. Your session is
  // the live truth for YOUR row; everyone else's stays as the roster has it.
  const isMe = (m) => (m.email || '').toLowerCase() === (user?.email || '').toLowerCase()
  const liveName = (m) => (isMe(m) ? user?.name || m.name : m.name)

  const MEMBER_COLUMNS = [
    {
      key: 'name',
      header: 'Member',
      icon: UserIcon,
      sortable: true,
      sortValue: (m) => liveName(m) || m.email,
      // ONE LINE, not two. Name and address stacked made every row twice as
      // tall for a roster that is mostly scanned by name — side by side keeps
      // the table compact and still shows the address in full. The email
      // truncates before the name does: on a narrow table the name is the part
      // you are looking for.
      render: (m) => (
        <div className="flex min-w-0 items-baseline gap-x-2">
          <span className="flex-none font-semibold">{liveName(m) || 'N/A'}</span>
          {isMe(m) && (
            <span className="flex-none rounded-full bg-primary-tint px-[7px] py-px text-[10px] font-bold uppercase tracking-[0.04em] text-primary-dark">You</span>
          )}
          <span className="min-w-0 truncate text-[12.5px] text-faint" title={m.email}>{m.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      icon: TagIcon,
      sortable: true,
      sortValue: (m) => ROLE_DEFS[normalizeRole(m.role)]?.rank ?? 0,
      render: (m) => {
        const target = normalizeRole(m.role)
        const isCustom = Array.isArray(m.permissions)
        const label = isCustom ? 'Custom' : ROLE_DEFS[target]?.label || m.role || '—'
        const hint = isCustom
          ? `Manually configured permissions (${ROLE_DEFS[target]?.label || target} base)`
          : ROLE_DEFS[target]?.blurb || ''
        const isSelf = (m.email || '').toLowerCase() === (user?.email || '').toLowerCase()
        // Editable only with the permission, never on yourself, and only for
        // members ranked strictly below you (an admin can't demote an owner).
        const editable =
          can('members.changeRole') && !isSelf && (ROLE_DEFS[target]?.rank ?? 0) < myRank
        if (!editable) {
          return (
            <span className="text-[13px] text-ink" title={hint}>
              {label}
            </span>
          )
        }
        return (
          <button
            type="button"
            title={`${hint} — click to edit`}
            aria-label={`Edit access for ${m.email}`}
            onClick={(e) => {
              e.stopPropagation()
              openEditor(m)
            }}
            className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-panel px-[10px] py-[5px] text-[13px] font-medium text-ink transition-colors hover:border-primary hover:text-primary-dark"
          >
            {label}
            <svg viewBox="0 0 24 24" fill="none" className="h-[13px] w-[13px] text-faint" aria-hidden="true">
              {/* coolicons: line/Edit_Pencil_01.svg */}
              <path d="M12 8.00012L4 16.0001V20.0001L8 20.0001L16 12.0001M12 8.00012L14.8686 5.13146L14.8704 5.12976C15.2652 4.73488 15.463 4.53709 15.691 4.46301C15.8919 4.39775 16.1082 4.39775 16.3091 4.46301C16.5369 4.53704 16.7345 4.7346 17.1288 5.12892L18.8686 6.86872C19.2646 7.26474 19.4627 7.46284 19.5369 7.69117C19.6022 7.89201 19.6021 8.10835 19.5369 8.3092C19.4628 8.53736 19.265 8.73516 18.8695 9.13061L18.8686 9.13146L16 12.0001M12 8.00012L16 12.0001" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (m) => (
        <StatusPill tone={STATUS_TONE[m.status] || 'neutral'}>
          {m.status}
        </StatusPill>
      ),
    },
    {
      key: 'invited_at',
      header: 'Invited',
      icon: CalendarIcon,
      sortable: true,
      // Read like every other timestamp in the app — "Aug 12, 2026, 3:33 AM",
      // with the relative age on hover. It used to print the raw ISO string in
      // a mono font, which is a storage format, not a date a person reads.
      render: (m) => {
        const when = formatDayTime(m.invited_at)
        if (!when) return <span className="text-[12.5px] text-faint">N/A</span>
        return (
          <span className="whitespace-nowrap text-[12.5px] text-dim" title={relativeTime(m.invited_at) || undefined}>
            {when}
          </span>
        )
      },
    },
    // Row actions (Change password · Sign in as · Remove). Every signed-in
    // member has at least one — changing their own password — so the column is
    // always there; each button then shows per-row within its boundary.
    ...(user
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (m) => {
              // Same boundary as the role editor: never yourself, never a peer
              // or superior (an admin can't remove/impersonate the owner).
              const isSelf = (m.email || '').toLowerCase() === (user?.email || '').toLowerCase()
              const rank = ROLE_DEFS[normalizeRole(m.role)]?.rank ?? 0
              const removable = can('members.remove') && !isSelf && rank < myRank
              const impersonable = canImpersonate && !isSelf && rank < myRank
              // Your own row: always (change_own_password checks your current
              // password). Anyone else: owner/admin, below you — the lambda
              // enforces the same ladder on set_password.
              const passwordable = isSelf || (canImpersonate && rank < myRank)
              if (!removable && !impersonable && !passwordable) return null
              return (
                <div className="flex items-center justify-end gap-x-1">
                  {passwordable && (
                    <button
                      className="grid h-[30px] w-[30px] place-items-center rounded-[7px] bg-transparent text-faint transition-colors hover:bg-panel hover:text-primary-dark [&>svg]:h-4 [&>svg]:w-4"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPasswordFor(m)
                      }}
                      title={isSelf ? 'Change your password' : `Change ${m.name || m.email}’s password`}
                      aria-label={isSelf ? 'Change your password' : `Change password for ${m.email}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        {/* coolicons: line/Lock.svg */}
                        <path d="M9.23047 9H7.2002C6.08009 9 5.51962 9 5.0918 9.21799C4.71547 9.40973 4.40973 9.71547 4.21799 10.0918C4 10.5196 4 11.0801 4 12.2002V17.8002C4 18.9203 4 19.4801 4.21799 19.9079C4.40973 20.2842 4.71547 20.5905 5.0918 20.7822C5.5192 21 6.07902 21 7.19694 21H16.8031C17.921 21 18.48 21 18.9074 20.7822C19.2837 20.5905 19.5905 20.2842 19.7822 19.9079C20 19.4805 20 18.9215 20 17.8036V12.1969C20 11.079 20 10.5192 19.7822 10.0918C19.5905 9.71547 19.2837 9.40973 18.9074 9.21799C18.4796 9 17.9203 9 16.8002 9H14.7689M9.23047 9H14.7689M9.23047 9C9.10302 9 9 8.89668 9 8.76923V6C9 4.34315 10.3431 3 12 3C13.6569 3 15 4.34315 15 6V8.76923C15 8.89668 14.8964 9 14.7689 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                  {impersonable && (
                    <button
                      className="grid h-[30px] w-[30px] place-items-center rounded-[7px] bg-transparent text-faint transition-colors hover:bg-panel hover:text-primary-dark [&>svg]:h-4 [&>svg]:w-4"
                      onClick={(e) => {
                        e.stopPropagation()
                        // impersonate() logs impersonate_start itself
                        // (auth.jsx) — logging here too wrote two rows per
                        // click once the type was whitelisted.
                        impersonate(m)
                        navigate('/')
                      }}
                      title={`Sign in as ${m.name || m.email} — your real session stays untouched`}
                      aria-label={`Sign in as ${m.email}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        {/* coolicons: line/User_Check.svg */}
                        <path d="M15 19C15 16.7909 12.3137 15 9 15C5.68629 15 3 16.7909 3 19M21 10L17 14L15 12M9 12C6.79086 12 5 10.2091 5 8C5 5.79086 6.79086 4 9 4C11.2091 4 13 5.79086 13 8C13 10.2091 11.2091 12 9 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                  {removable && (
                    <button
                      className="grid h-[30px] w-[30px] place-items-center rounded-[7px] bg-transparent text-faint transition-colors hover:bg-[#fef2f2] hover:text-[#dc2626] dark:hover:bg-[rgba(220,38,38,0.16)] [&>svg]:h-4 [&>svg]:w-4"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemove(m)
                      }}
                      aria-label={`Remove ${m.email}`}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              )
            },
          },
        ]
      : []),
  ]

  const InviteButton = ({ className = '' }) => (
    <button
      className={`inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark ${className}`}
      onClick={handleInvite}
    >
      <PlusIcon className="h-4 w-4" />
      Invite team
    </button>
  )

  return (
    // Capped so the roster doesn't sprawl on wide monitors, but flush left so
    // the header lines up with every other page (never mx-auto). At least the
    // pane's height, but free to grow: the table fits its rows (fitToContent),
    // and a page pinned to h-full let a tall roster spill past its own padding.
    <div className="flex min-h-full w-full max-w-[1100px] flex-col">
      <PageHeader
        title="Collaboration"
        subtitle="Your team and what they can access. Invited users get an email and sign up to set their password."
        actions={
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              onClick={() => setLegendOpen(true)}
              className="inline-flex items-center rounded-[9px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
            >
              What each role can do
            </button>
            {can('members.invite') ? <InviteButton /> : null}
          </div>
        }
      />

      {notice && noticeTone === 'success' && (
        <div className="mb-4 inline-block rounded-[10px] border border-[#bbf7d0] bg-[#f0fdf4] px-[14px] py-2 text-[13px] font-semibold text-[#15803d] dark:border-[rgba(34,197,94,0.35)] dark:bg-[rgba(34,197,94,0.12)] dark:text-[#4ade80]">
          ✓ {notice}
        </div>
      )}
      {notice && noticeTone === 'warning' && (
        <div role="alert" className="mb-4 inline-block rounded-[10px] border border-[#fde68a] bg-[#fefce8] px-[14px] py-2 text-[13px] font-semibold text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]">
          {notice}
        </div>
      )}

      <div className={loading ? 'flex min-h-0 flex-1 flex-col' : 'min-h-0 flex-1'}>
        {loading ? (
          <PanelLoading label="Reading the team…" />
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center gap-y-3 rounded-[12px] border border-dashed border-line bg-panel px-6 py-16 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-[14px] border border-line bg-surface text-primary [&>svg]:h-[26px] [&>svg]:w-[26px]">
              <CollaborationIcon />
            </div>
            <h2 className="m-0 text-[18px]">No teammates yet</h2>
            <p className="m-0 max-w-[380px] text-[14px] leading-[1.5] text-dim">
              {can('members.invite')
                ? 'Invite your first teammate to work leads and deals together — use “Invite team” up top.'
                : 'No teammates on the roster yet.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
            <section className="mb-3 text-[13px] text-dim">
              {members.length} team member{members.length === 1 ? '' : 's'}
            </section>
            <DataTable
              columns={MEMBER_COLUMNS}
              rows={members}
              rowKey={(m) => m.email}
              searchable
              searchPlaceholder="Search team…"
              searchKeys={['name', 'email', 'role']}
              emptyMessage="No teammates match."
              fitToContent
            />
          </div>
        )}
      </div>

      {legendOpen && <RolesLegendModal onClose={() => setLegendOpen(false)} />}

      {passwordFor && (
        <PasswordModal member={passwordFor} self={isMe(passwordFor)} onClose={() => setPasswordFor(null)} onDone={handlePasswordDone} />
      )}

      {editing && draft && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.45)] p-4 dark:bg-[rgba(0,0,0,0.6)]"
          onClick={() => !saving && setEditing(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Role & permissions for ${editing.email}`}
            className="max-h-[88vh] w-full max-w-[860px] overflow-y-auto rounded-[14px] border border-line bg-surface p-6 shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex items-center gap-x-3">
              <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[11px] bg-[#18181b] text-[13px] font-semibold tracking-[0.02em] text-white">
                {initialsOf(editing.name || editing.email)}
              </span>
              <div className="min-w-0">
                <h3 className="m-0 text-[16px] font-semibold text-ink">
                  Role &amp; permissions — {editing.name || editing.email}
                </h3>
                <div className="text-[12.5px] text-dim">{editing.email}</div>
              </div>
            </div>
            <p className="m-0 mb-4 text-[12.5px] text-dim">
              Click a preset to auto-fill, or toggle individual permissions. Click a column header or
              a row’s checkbox to toggle the whole set.
            </p>

            <RolePermissions
              value={draft}
              onChange={setDraft}
              presets={assignable}
              grantable={myPerms}
            />

            <div className="mt-5 flex justify-end gap-x-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setEditing(null)}
                className="inline-flex items-center rounded-[9px] border border-line bg-transparent px-[14px] py-[8px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleAccessSave}
                className="inline-flex items-center rounded-[9px] bg-primary px-[16px] py-[8px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
